import { useEffect } from 'react';

/**
 * Triggers `onTrigger` when:
 *   - the user types the sequence "stat" (anywhere outside an input)
 *   - the device is shaken (mobile)
 *
 * Used by the booth page to surface the debug stats badge that's
 * otherwise hidden.
 */
export function useEasterEgg(onTrigger: () => void): void {
  useEffect(() => {
    let buf = '';
    const TARGET = 'stat';
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      if (e.key.length !== 1) return;
      buf = (buf + e.key.toLowerCase()).slice(-TARGET.length);
      if (buf === TARGET) {
        buf = '';
        onTrigger();
      }
    };
    window.addEventListener('keydown', onKey);

    let lastShakeAt = 0;
    const onMotion = (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const m = Math.sqrt(
        (a.x ?? 0) * (a.x ?? 0) +
          (a.y ?? 0) * (a.y ?? 0) +
          (a.z ?? 0) * (a.z ?? 0),
      );
      // Gravity at rest is ~9.8; a shake spikes well past 22.
      if (m > 22 && Date.now() - lastShakeAt > 1200) {
        lastShakeAt = Date.now();
        onTrigger();
      }
    };
    window.addEventListener('devicemotion', onMotion);

    // iOS 13+ requires explicit permission for devicemotion (user gesture).
    // We attach a one-shot touch listener that requests permission on the
    // user's first touch — silently fails if they decline.
    type WithPermission = typeof DeviceMotionEvent & {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    const M = DeviceMotionEvent as WithPermission;
    const needsPermission = typeof M?.requestPermission === 'function';
    let removeGrant: (() => void) | undefined;
    if (needsPermission) {
      const grant = () => {
        M.requestPermission?.().catch(() => undefined);
      };
      window.addEventListener('touchstart', grant, { once: true, passive: true });
      removeGrant = () => window.removeEventListener('touchstart', grant);
    }

    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('devicemotion', onMotion);
      removeGrant?.();
    };
  }, [onTrigger]);
}
